import { useSearch } from '@tanstack/react-router';

/**
 * Read search params that no route declares.
 *
 * `useSearch({ strict: false })` is typed as the UNION of every route's
 * declared search params. Anything outside that union — a one-shot notice flag,
 * a token on a link we did not generate — is not on the returned type, so
 * reading it is a compile error (TS2339 for a named property, TS7053 for an
 * index) and every call site ends up writing its own `as` to get past it.
 *
 * Those assertions are load-bearing and eslint says they are not.
 * `@typescript-eslint/no-unnecessary-type-assertion` reports each one, and it
 * is simply wrong: removing them breaks the build. That has already cost real
 * damage once — an `eslint --fix` deleted the assertion in ResetPasswordPage
 * and took `search.token` with it, which is the whole reset flow.
 *
 * So the assertion lives here, once, with the disable next to the reason,
 * instead of being re-derived (and re-deleted) at each call site:
 *
 *     const { token } = useLooseSearch<{ token?: string }>();
 *
 * `Partial` because the caller is asking about params that may well be absent —
 * the type should say so rather than let a missing flag read as present.
 */
export function useLooseSearch<
  T extends Record<string, unknown>,
>(): Partial<T> {
  // Widened through `unknown` first, deliberately. Casting the hook's result
  // inline makes TanStack infer its own TSelected from the expected return
  // type, and the overload then rejects `{ strict: false }` outright (TS2345).
  const search: unknown = useSearch({ strict: false });
  return search as Partial<T>;
}
